// src/pages/EmployeesReviews.js
import React from 'react';
import './HumanResources.css';

function EmployeesReviews() {
  return (
    <div className="hr-page">
      <h1>NESIM EMPLOYEES ANNUAL PERFORMANCE REVIEWS</h1>
      <p>
        This framework provides a starting point for designing Nesim's annual performance review system. 
        The specific details and weighting of each area should be further customized based on Nesim's strategic goals and priorities. 
        It is important to have a process for providing feedback, setting development goals, and conducting regular performance discussions 
        throughout the year, not just during the annual review.
      </p>

      {/* 1. Accountant */}
      <h2>1. Accountant</h2>
      <h3>Potential Performance Review Areas:</h3>
      <ul>
        <li>
          <strong>Accuracy of Financial Statements and Records:</strong> Evaluate the number of errors or discrepancies found in financial reports and transaction records.
        </li>
        <li>
          <strong>Timeliness of Reporting:</strong> Assess adherence to deadlines for daily, weekly, and monthly financial reports.
        </li>
        <li>
          <strong>Budget Management:</strong> Review the effectiveness in assisting with budget preparation and the ability to identify and analyze budget variances.
        </li>
        <li>
          <strong>Tax Compliance:</strong> Evaluate the accuracy and timely filing of tax returns, ensuring compliance with KRA regulations.
        </li>
        <li>
          <strong>Efficiency and Cost Improvement:</strong> Assess contributions to cost analysis and identification of areas for improvement.
        </li>
        <li>
          <strong>Financial Analysis and Insights:</strong> Review the quality and usefulness of financial analysis provided for decision-making.
        </li>
        <li>
          <strong>Collaboration and Support:</strong> Evaluate feedback from other departments on the accountant’s support and collaboration.
        </li>
        <li>
          <strong>Adherence to Procedures:</strong> Ensure all financial practices adhere to regulations.
        </li>
      </ul>
      <h3>KPIs:</h3>
      <ul>
        <li>Budget Variance</li>
        <li>Accuracy Rate of Financial Data Entry</li>
        <li>Timeliness of Financial Reporting</li>
        <li>Number of Tax Compliance Issues</li>
        <li>Cost Savings Identified and Implemented</li>
      </ul>

      {/* 2. Head of Supply Chain */}
      <h2>2. Head of Supply Chain</h2>
      <h3>Potential Performance Review Areas:</h3>
      <ul>
        <li>
          <strong>Procurement Effectiveness:</strong> Evaluate the efficiency and cost-effectiveness of the procurement process.
        </li>
        <li>
          <strong>Supplier Relationship Management:</strong> Assess the quality of relationships with suppliers and their impact on supply chain performance.
        </li>
        <li>
          <strong>Inventory Management:</strong> Review the effectiveness in preventing shortages and overstocking.
        </li>
        <li>
          <strong>Logistics and Distribution:</strong> Evaluate the efficiency and timeliness of imports, exports, and product delivery to customers.
        </li>
        <li>
          <strong>Customs Clearance:</strong> Assess the smoothness and timeliness of the customs clearing process.
        </li>
        <li>
          <strong>Sales Support:</strong> Review the effectiveness in issuing proforma invoices and preparing necessary sales documentation.
        </li>
        <li>
          <strong>Interdepartmental Collaboration:</strong> Evaluate collaboration with finance and sales departments.
        </li>
        <li>
          <strong>Warehouse and Stock Accuracy:</strong> Assess the accuracy of warehouse records and stock counts.
        </li>
      </ul>
      <h3>KPIs:</h3>
      <ul>
        <li>Customer Satisfaction (related to timely delivery)</li>
        <li>Products Reaching Customers on Time</li>
        <li>Inventory Turnover Rate</li>
        <li>Stockout Rate</li>
        <li>Cost of Goods Sold</li>
        <li>Efficiency of Customs Clearance (e.g., time taken)</li>
        <li>Accuracy of Warehouse Inventory</li>
      </ul>

      {/* 3. Office Administrator */}
      <h2>3. Office Administrator</h2>
      <h3>Potential Performance Review Areas:</h3>
      <ul>
        <li>
          <strong>Efficiency of Administrative Tasks:</strong> Evaluate the timely and accurate completion of administrative duties.
        </li>
        <li>
          <strong>Stakeholder Communication:</strong> Assess the effectiveness and professionalism of communication with suppliers and forwarding agents.
        </li>
        <li>
          <strong>Supervision of Loading and Delivery:</strong> Review the efficiency and accuracy of product loading and delivery processes.
        </li>
        <li>
          <strong>Database and Record Management:</strong> Evaluate the accuracy and organization of company records.
        </li>
        <li>
          <strong>Office Supplies Management:</strong> Assess the effectiveness in tracking and ensuring adequate office supplies.
        </li>
        <li>
          <strong>Report and Presentation Preparation:</strong> Review the quality and timeliness of reports and presentations.
        </li>
        <li>
          <strong>Supervision of Casual Employees:</strong> Evaluate the effectiveness in managing casual employees.
        </li>
        <li>
          <strong>Support to Chief Accountant:</strong> Assess the quality and timeliness of assistance provided to the chief accountant.
        </li>
        <li>
          <strong>Organizational Skills:</strong> Review the ability to manage tasks and information effectively.
        </li>
        <li>
          <strong>Teamwork:</strong> Evaluate contributions as a team player.
        </li>
      </ul>
      <h3>KPIs:</h3>
      <ul>
        <li>Timeliness and Accuracy of Reports and Presentations</li>
        <li>Efficiency of Record-Keeping Systems</li>
        <li>Availability of Office Supplies</li>
        <li>Feedback from Stakeholders on Communication</li>
        <li>Smoothness of Loading and Delivery Operations</li>
      </ul>

      {/* 4. Technical Officer */}
      <h2>4. Technical Officer</h2>
      <h3>Potential Performance Review Areas:</h3>
      <ul>
        <li>
          <strong>Effectiveness of Trials and Technical Support:</strong> Evaluate the success rate of trials and the quality of technical support provided to customers.
        </li>
        <li>
          <strong>Quality Assurance of Raw Materials:</strong> Assess the effectiveness in ensuring raw materials meet set standards.
        </li>
        <li>
          <strong>Interdepartmental Coordination:</strong> Review the efficiency of coordination with procurement and finance.
        </li>
        <li>
          <strong>Contribution to New Product Development:</strong> Evaluate involvement and impact on the development of new products.
        </li>
        <li>
          <strong>Customer Relationship Management:</strong> Assess the effectiveness in understanding and addressing customer needs.
        </li>
        <li>
          <strong>System Performance Improvement:</strong> Review the quality and impact of suggestions for system improvements.
        </li>
        <li>
          <strong>Timeliness and Quality of Technical Reports:</strong> Evaluate the preparation of technical summaries.
        </li>
        <li>
          <strong>Creating Partnerships:</strong> Collaborate with different teams for operational excellence and new product development.
        </li>
        <li>
          <strong>Effectiveness of Training Programs:</strong> Assess the impact of training provided to staff.
        </li>
        <li>
          <strong>Collaboration:</strong> Evaluate teamwork and collaboration with other departments.
        </li>
      </ul>
      <h3>KPIs:</h3>
      <ul>
        <li>New Orders for Raw Materials</li>
        <li>Feedback from Team Members and Other Stakeholders</li>
        <li>Revenue Generated with Respect to Previous Years</li>
        <li>Cost-Effectiveness of Technical Solutions</li>
        <li>Customer Satisfaction with Technical Support</li>
        <li>Success Rate of Product Trials</li>
        <li>Number of Technical Issues Resolved Effectively</li>
      </ul>

      {/* 5. Managing Director */}
      <h2>5. Managing Director</h2>
      <h3>Potential Performance Review Areas:</h3>
      <ul>
        <li>
          <strong>Strategic Planning and Execution:</strong> Evaluate the development and successful implementation of strategic plans.
        </li>
        <li>
          <strong>Financial Performance:</strong> Review key financial metrics such as revenue growth, profit margin, and gross margin.
        </li>
        <li>
          <strong>Operational Efficiency:</strong> Assess the effectiveness in overseeing and improving day-to-day operations.
        </li>
        <li>
          <strong>Leadership and Team Management:</strong> Evaluate the ability to lead and motivate the senior management team and foster a positive company culture.
        </li>
        <li>
          <strong>Stakeholder Relationship Management:</strong> Review the effectiveness in managing relationships with clients, stakeholders, and the board.
        </li>
        <li>
          <strong>Growth and Market Expansion:</strong> Assess the success in identifying and pursuing growth opportunities and entering new markets.
        </li>
        <li>
          <strong>Regulatory Compliance:</strong> Evaluate the company's adherence to relevant regulations.
        </li>
        <li>
          <strong>Risk Management:</strong> Assess the ability to identify and mitigate key business risks.
        </li>
      </ul>
      <h3>KPIs:</h3>
      <ul>
        <li>Revenue Growth</li>
        <li>Profit Margin</li>
        <li>Customer Satisfaction</li>
        <li>Employee Satisfaction</li>
        <li>Gross Margin</li>
        <li>Overall Financial Performance</li>
        <li>Employee Engagement</li>
        <li>Return on Investment</li>
        <li>Market Share</li>
        <li>Successful Entry into New Markets</li>
      </ul>

      {/* 6. Business Development Manager */}
      <h2>6. Business Development Manager</h2>
      <h3>Potential Performance Review Areas:</h3>
      <ul>
        <li>
          <strong>Revenue Generation & Sales Targets:</strong> Did they meet or exceed their assigned sales quotas and revenue targets? What was the quality of the revenue generated (e.g., profitability, strategic fit)? How effective were their sales strategies and execution?
        </li>
        <li>
          <strong>New Business Acquisition:</strong> How successful were they in identifying, pursuing, and closing new business opportunities? What was the value and strategic importance of the new clients acquired? How effective were their prospecting and lead generation efforts?
        </li>
        <li>
          <strong>Client Relationship Management:</strong> How strong are their relationships with key clients? Are clients satisfied with the level of service and engagement? Have they successfully retained and grown business with existing clients?
        </li>
        <li>
          <strong>Market Knowledge & Opportunity Identification:</strong> How well do they understand the target market, industry trends, and competitive landscape? Are they proactive in identifying new market opportunities and potential partnerships? How effective are their market research and analysis skills?
        </li>
        <li>
          <strong>Strategic Planning & Execution:</strong> How well do they contribute to the development and execution of business development strategies? Are they able to develop and implement effective sales plans? How well do they align their activities with the overall company objectives?
        </li>
        <li>
          <strong>Pipeline Management:</strong> How effectively do they manage their sales pipeline, ensuring a consistent flow of opportunities? Is their pipeline accurate and well-documented? Do they effectively forecast future sales?
        </li>
        <li>
          <strong>Collaboration & Teamwork:</strong> How effectively do they collaborate with internal teams (e.g., sales, marketing, product)? Are they a supportive and contributing team member?
        </li>
        <li>
          <strong>Communication & Presentation Skills:</strong> How clear, concise, and persuasive is their communication (written and verbal)? How effective are their presentation and negotiation skills?
        </li>
        <li>
          <strong>Professional Development & Initiative:</strong> Are they proactive in seeking opportunities for professional growth and development? Do they take initiative and demonstrate a strong work ethic? Are they adaptable and open to feedback?
        </li>
      </ul>
      <h3>KPIs:</h3>
      <ul>
        <li><strong>Revenue & Sales Focused KPIs:</strong></li>
        <ul>
          <li>New Business Revenue</li>
          <li>Sales Growth Rate</li>
          <li>Average Deal Size</li>
          <li>Sales Cycle Length</li>
          <li>Conversion Rate (Lead to Opportunity)</li>
          <li>Conversion Rate (Opportunity to Close)</li>
          <li>Revenue per New Client</li>
          <li>Customer Lifetime Value (CLTV) of New Clients</li>
          <li>Gross Profit Margin from New Business</li>
        </ul>
        <li><strong>Activity & Pipeline Focused KPIs:</strong></li>
        <ul>
          <li>Number of New Leads Generated</li>
          <li>Number of Qualified Leads</li>
          <li>Number of Meetings/Presentations Conducted</li>
          <li>Number of Proposals/Quotes Submitted</li>
          <li>Pipeline Value</li>
          <li>Pipeline Stage Distribution</li>
          <li>Sales Velocity</li>
          <li>Time Spent on Key Activities</li>
        </ul>
        <li><strong>Relationship & Strategic Focused KPIs:</strong></li>
        <ul>
          <li>Customer Acquisition Cost (CAC)</li>
          <li>Customer Retention Rate (for newly acquired clients)</li>
          <li>Partner Acquisition & Performance</li>
          <li>Market Penetration Rate (in target markets)</li>
          <li>Network Growth</li>
          <li>Contribution to Strategic Initiatives</li>
          <li>Feedback from Internal Teams & Clients</li>
        </ul>
      </ul>
    </div>
  );
}

export default EmployeesReviews;
